import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * CONFY Textarea Design System
 *
 * A unified textarea component that matches the Input component styling.
 * Supports labels, error messages, hints, and character counting.
 *
 * Variants:
 * - default: Standard gray border with blue focus ring
 * - error: Red border with red focus ring (auto-applied when error prop is set)
 * - success: Green border with green focus ring
 *
 * @example
 * <Textarea
 *   label="Description"
 *   value={description}
 *   onChange={(e) => setDescription(e.target.value)}
 *   placeholder="Enter description..."
 *   rows={4}
 *   maxLength={500}
 *   showCharCount
 *   hint="Brief description of the event"
 * />
 */
const textareaVariants = cva(
  "w-full px-4 py-3 border rounded-lg text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 placeholder:text-gray-400 resize-none outline-none",
  {
    variants: {
      variant: {
        default:
          "border-gray-300 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent",
        error:
          "border-red-500 focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:border-transparent bg-red-50",
        success:
          "border-green-500 focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:border-transparent",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "className">,
    VariantProps<typeof textareaVariants> {
  /** Label text displayed above the textarea */
  label?: string
  /** Error message displayed below the textarea (auto-applies error variant) */
  error?: string
  /** Hint text displayed below the textarea (hidden when error is shown) */
  hint?: string
  /** Show character count when maxLength is set */
  showCharCount?: boolean
  /** Additional class names for the textarea element */
  className?: string
  /** Additional class names for the container div */
  containerClassName?: string
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      variant,
      label,
      error,
      hint,
      showCharCount,
      maxLength,
      containerClassName,
      id,
      value,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const textareaId = id || generatedId
    const errorId = error ? `${textareaId}-error` : undefined
    const hintId = hint ? `${textareaId}-hint` : undefined

    // Auto-detect error variant if error message is provided
    const resolvedVariant = error ? "error" : variant

    // Character count
    const currentLength = typeof value === "string" ? value.length : 0
    const showCount = showCharCount && maxLength

    return (
      <div className={cn("w-full", containerClassName)}>
        {label && (
          <label
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <textarea
          id={textareaId}
          ref={ref}
          value={value}
          maxLength={maxLength}
          className={cn(textareaVariants({ variant: resolvedVariant }), className)}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={
            [errorId, hintId].filter(Boolean).join(" ") || undefined
          }
          {...props}
        />
        <div className="flex justify-between mt-1">
          <div className="flex-1">
            {error && (
              <p id={errorId} className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            {hint && !error && (
              <p id={hintId} className="text-sm text-gray-500">
                {hint}
              </p>
            )}
          </div>
          {showCount && (
            <span
              className={cn(
                "text-xs ml-2",
                currentLength >= maxLength ? "text-red-500" : "text-gray-400"
              )}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      </div>
    )
  }
)

Textarea.displayName = "Textarea"

export { Textarea, textareaVariants }
